import api from './client';

export interface MaintenanceLookupInvestor {
  id: number;
  name: string;
  email: string | null;
  phone: string | null;
  address: string | null;
}

export interface MaintenanceLookupPowerPlant {
  id: number;
  name: string;
  investor_id: number;
  investor_name: string;
}

export interface MaintenanceLookupOption {
  id: string;
  name: string;
}

export interface MaintenanceLookups {
  investors: MaintenanceLookupInvestor[];
  powerPlants: MaintenanceLookupPowerPlant[];
  stages: MaintenanceLookupOption[];
  contractKinds: MaintenanceLookupOption[];
}

export interface MaintenanceDashboard {
  exchange_rate?: { rate: number; date: string; stale: boolean } | null;
  converted_monthly_try_total?: number | null;
  converted_monthly_usd_total?: number | null;
  converted_yearly_try_total?: number | null;
  converted_yearly_usd_total?: number | null;
  total_contracts: number;
  draft_contracts: number;
  proposal_contracts: number;
  active_contracts: number;
  expiring_30_days: number;
  total_plants: number;
  monthly_try_total: number;
  monthly_usd_total: number;
  yearly_try_total: number;
  yearly_usd_total: number;
}

export interface MaintenanceContractRow {
  id: number;
  uuid_id: string;
  contract_kind: string;
  stage: string;
  reference_code: string | null;
  investor_id: number | null;
  investor_name_snapshot: string;
  contact_email: string | null;
  contact_phone: string | null;
  location_text: string | null;
  start_date: string | null;
  duration_years: number | null;
  end_date: string | null;
  scope_summary: string | null;
  vat_note: string | null;
  notes: string | null;
  updated_on: string;
  line_count: number;
  plant_names: string | null;
  installed_power_texts: string | null;
  monthly_fee_usd_total: number;
  monthly_fee_try_total: number;
  yearly_fee_usd_total: number;
  yearly_fee_try_total: number;
  scope_display: string;
  remaining_days: number | null;
}

export interface MaintenanceContractLine {
  id?: number;
  power_plant_id: number | null;
  power_plant_name_snapshot: string;
  installed_power_text: string | null;
  monthly_fee_usd: number;
  monthly_fee_try: number;
  yearly_fee_usd: number;
  yearly_fee_try: number;
  vat_note: string | null;
  line_notes: string | null;
  sort_order: number;
}

export interface MaintenanceDocument {
  id: number;
  document_kind: string;
  title: string;
  file_name: string | null;
  path_name: string | null;
  mime_type: string | null;
  created_by: number | null;
  created_by_name: string | null;
  created_on: string;
}

export interface MaintenanceHistoryItem {
  id: number;
  event_type: string;
  details: string | null;
  changed_by: number | null;
  changed_by_name: string | null;
  created_on: string;
}

export interface MaintenanceContractDetail extends Omit<MaintenanceContractRow, 'line_count' | 'plant_names' | 'monthly_fee_usd_total' | 'monthly_fee_try_total' | 'yearly_fee_usd_total' | 'yearly_fee_try_total' | 'updated_on'> {
  created_by: number | null;
  created_by_name: string | null;
  created_on: string;
  updated_on: string;
  lines: MaintenanceContractLine[];
  documents: MaintenanceDocument[];
  history: MaintenanceHistoryItem[];
}

export interface MaintenanceContractPayload {
  contractKind: string;
  stage: string;
  referenceCode?: string | null;
  investorId?: number | null;
  investorNameSnapshot: string;
  contactEmail?: string | null;
  contactPhone?: string | null;
  locationText?: string | null;
  startDate?: string | null;
  durationYears?: number | null;
  endDate?: string | null;
  scopeSummary?: string | null;
  vatNote?: string | null;
  notes?: string | null;
  lines: Array<{
    powerPlantId?: number | null;
    powerPlantName: string;
    installedPower?: string | null;
    monthlyFeeUsd?: number;
    monthlyFeeTry?: number;
    yearlyFeeUsd?: number;
    yearlyFeeTry?: number;
    vatNote?: string | null;
    notes?: string | null;
    sortOrder?: number;
  }>;
}

export async function fetchMaintenanceLookups(): Promise<MaintenanceLookups> {
  const res = await api.get<MaintenanceLookups>('/maintenance/lookups');
  return res.data;
}

export async function fetchMaintenanceDashboard(): Promise<MaintenanceDashboard> {
  const res = await api.get<MaintenanceDashboard>('/maintenance/dashboard');
  return res.data;
}

export async function fetchMaintenanceContracts(params?: { stage?: string; q?: string }): Promise<MaintenanceContractRow[]> {
  const res = await api.get<MaintenanceContractRow[]>('/maintenance/contracts', {
    params,
  });
  return res.data;
}

export async function fetchMaintenanceContractDetail(uuidId: string): Promise<MaintenanceContractDetail> {
  const res = await api.get<MaintenanceContractDetail>(`/maintenance/contracts/${uuidId}`);
  return res.data;
}

export async function createMaintenanceContract(payload: MaintenanceContractPayload): Promise<MaintenanceContractDetail> {
  const res = await api.post<MaintenanceContractDetail>('/maintenance/contracts', payload);
  return res.data;
}

export async function updateMaintenanceContract(uuidId: string, payload: MaintenanceContractPayload): Promise<MaintenanceContractDetail> {
  const res = await api.put<MaintenanceContractDetail>(`/maintenance/contracts/${uuidId}`, payload);
  return res.data;
}

export async function deleteMaintenanceContract(uuidId: string): Promise<void> {
  await api.delete(`/maintenance/contracts/${uuidId}`);
}
